import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { User, Mail, Phone, Shield, Trash2, FileText, Lock, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/gj";
import { useAuth } from "../lib/auth";

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  if (user === null) return null; // still loading
  if (!user || !user.id) return <Navigate to="/login" replace />;

  const handleDelete = async () => {
    if (confirmText.trim().toUpperCase() !== "DELETE") {
      toast.error('Please type "DELETE" to confirm.');
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/auth/me");
      toast.success("Your account has been deleted.");
      await logout();
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not delete account");
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-14" data-testid="profile-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-md bg-[#0A192F] grid place-items-center text-white">
          <User className="w-5 h-5" />
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A192F]">
            My Account
          </h1>
          <p className="text-sm text-[#4A5568]">
            Manage your profile, privacy and legal preferences.
          </p>
        </div>
      </div>

      <div className="gj-divider-gold mt-4 mb-8" />

      {/* Profile details */}
      <section className="bg-white border border-[#0A192F]/10 rounded-xl p-6 mb-6">
        <h2 className="font-heading text-lg font-bold text-[#0A192F] mb-4">
          Profile Details
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Name</Label>
            <Input value={user.name || ""} disabled data-testid="profile-name" />
          </div>
          <div>
            <Label>Email</Label>
            <div className="flex items-center gap-2 mt-1 text-sm">
              <Mail className="w-4 h-4 text-[#718096]" />
              <span className="text-[#0A192F]" data-testid="profile-email">{user.email}</span>
            </div>
          </div>
          <div>
            <Label>Phone</Label>
            <div className="flex items-center gap-2 mt-1 text-sm">
              <Phone className="w-4 h-4 text-[#718096]" />
              <span className="text-[#0A192F]">{user.phone || "—"}</span>
            </div>
          </div>
          <div>
            <Label>Role</Label>
            <div className="flex items-center gap-2 mt-1 text-sm">
              <Shield className="w-4 h-4 text-[#F97316]" />
              <span className="text-[#0A192F] uppercase tracking-wide text-xs font-bold">
                {user.role}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Legal */}
      <section className="bg-white border border-[#0A192F]/10 rounded-xl p-6 mb-6">
        <h2 className="font-heading text-lg font-bold text-[#0A192F] mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#F97316]" /> Legal &amp; Policies
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Link
            to="/terms"
            data-testid="profile-terms-link"
            className="border border-[#0A192F]/10 rounded-lg p-3 text-sm font-medium text-[#0A192F] hover:border-[#F97316] hover:text-[#F97316] transition-colors"
          >
            Terms &amp; Conditions
          </Link>
          <Link
            to="/privacy"
            data-testid="profile-privacy-link"
            className="border border-[#0A192F]/10 rounded-lg p-3 text-sm font-medium text-[#0A192F] hover:border-[#F97316] hover:text-[#F97316] transition-colors"
          >
            Privacy Policy
          </Link>
          <Link
            to="/disclaimer"
            data-testid="profile-disclaimer-link"
            className="border border-[#0A192F]/10 rounded-lg p-3 text-sm font-medium text-[#0A192F] hover:border-[#F97316] hover:text-[#F97316] transition-colors"
          >
            Disclaimer
          </Link>
        </div>
      </section>

      {/* Danger zone */}
      <section className="bg-white border-2 border-red-200 rounded-xl p-6">
        <h2 className="font-heading text-lg font-bold text-red-700 mb-2 flex items-center gap-2">
          <Lock className="w-5 h-5" /> Danger Zone
        </h2>
        <p className="text-sm text-[#4A5568] mb-4 leading-relaxed">
          Deleting your account is permanent. Your profile will be marked for
          deletion and any active car listings you own will be removed from the
          public marketplace. This action cannot be undone.
        </p>
        <Button
          variant="destructive"
          onClick={() => setConfirmOpen(true)}
          data-testid="profile-delete-account-btn"
          className="bg-red-600 hover:bg-red-700 text-white"
        >
          <Trash2 className="w-4 h-4 mr-2" /> Delete Account
        </Button>
      </section>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" /> Delete your account?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#4A5568] leading-relaxed">
              This will permanently mark your Gaddi Junction account for
              deletion, log you out, and hide any listings you own. Type{" "}
              <span className="font-bold text-red-700">DELETE</span> below to
              confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder='Type "DELETE" to confirm'
            data-testid="delete-account-confirm-input"
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-account-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              data-testid="delete-account-confirm-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
